import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => { 
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    document.querySelectorAll('.variant-description').forEach(el => el.classList.add('hidden'));
    const active = document.getElementById('var-desc-' + variantId.value);
    if (active) active.classList.remove('hidden');

  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}












// Wait for the entire page content to load before running the script
// document.addEventListener('DOMContentLoaded', () => {

//     // --- Configuration ---
//     // IMPORTANT: The button ID provided in your HTML is very specific to your store/theme version.
//     const ADD_TO_CART_BUTTON_ID = 'BuyButtons-ProductSubmitButton-AaVNieDRtcFQvN0pqc__add-to-cart';
    
//     // Define the button texts
//     const originalButtonText = 'Add to cart';
//     const disabledButtonText = 'Fill details to proceed';

//     // Select the required custom inputs
//     const namesTitlesInput = document.getElementById('names-titles');
//     // We select the FIRST file input with this ID for the logo upload
//     // const logoUploadInput = document.getElementById('artwork-upload1'); 
//     const sendLogoLaterCheckbox = document.getElementById('send-logo-later');
    
//     const addToCartButton = document.getElementById(ADD_TO_CART_BUTTON_ID);
    
//     // Select the element inside the button that holds the text
//     const addToCartTextContent = addToCartButton ? addToCartButton.querySelector('.add-to-cart-text__content') : null;
    
//     // Select quantity controls and the element to show/hide
//     const quantityInput = document.querySelector('input[name="quantity"]');
//     const temWrpElement = document.getElementById('tem-wrp');

//     setTimeout(() => {
//     const logoUploadInput = document.querySelector('.filepond--browser')
//     // Stop execution if any critical element is missing
//     if (!namesTitlesInput || !logoUploadInput || !sendLogoLaterCheckbox || !addToCartButton || !addToCartTextContent) {
//         console.error('Custom validation script: One or more required elements were not found.');
//         return;
//     }

//     /**
//      * Checks if all mandatory fields meet the submission requirements.
//      * Requirements:
//      * 1. Names & Titles must contain content.
//      * 2. EITHER a logo must be uploaded OR the "Send Logo Later" checkbox must be checked.
//      * @returns {boolean} True if all requirements are met, false otherwise.
//      */
//     function validateCustomFields() {
//         // 1. Check Names & Titles (must have content)
//         const isNamesTitlesFilled = namesTitlesInput.value.trim().length > 0;

//         // 2. Check Logo Requirement
//         const isLogoUploaded = logoUploadInput.files.length > 0;
//         const isSendingLaterChecked = sendLogoLaterCheckbox.checked;

//         // Requirement is met if EITHER logo is uploaded OR checkbox is checked
//         const isLogoRequirementMet = isLogoUploaded || isSendingLaterChecked;

//         // Return true only if BOTH primary requirements are met
//         return isNamesTitlesFilled && isLogoRequirementMet;
//     }

//     /**
//      * Updates the state and appearance of the Add to Cart button based on field validation.
//      */
//     function updateButtonState() {
//         const isValid = validateCustomFields();

//         if (isValid) {
//             // ENABLED STATE
//             addToCartButton.disabled = false;
//             addToCartButton.style.opacity = '1';
//             // Set cursor back to normal pointer
//             addToCartButton.style.cursor = 'pointer'; 
            
//             // Restore original text
//             if (addToCartTextContent.textContent !== originalButtonText) {
//                 addToCartTextContent.textContent = originalButtonText;
//             }

//         } else {
//             // DISABLED STATE
//             addToCartButton.disabled = true;
//             addToCartButton.style.opacity = '0.6'; // Dim the button
//             addToCartButton.style.cursor = 'not-allowed'; // Show 'not-allowed' cursor
            
//             // Change text to prompt user for action
//             if (addToCartTextContent.textContent !== disabledButtonText) {
//                 addToCartTextContent.textContent = disabledButtonText;
//             }
//         }
//     }

//     /**
//      * Toggles visibility of #tem-wrp based on quantity value
//      * Shows if quantity > 1, hides if quantity = 1
//      */
//     function updateTemWrpVisibility() {
//         if (!temWrpElement) {
//             console.warn('tem-wrp element not found');
//             return;
//         }
        
//         if (!quantityInput) {
//             console.warn('quantity input not found');
//             return;
//         }
        
//         const quantity = parseInt(quantityInput.value, 10);
//         console.log('Current quantity:', quantity);
        
//         if (quantity > 1) {
//             console.log('Showing tem-wrp');
//             temWrpElement.style.display = 'block';
//             temWrpElement.classList.remove('hidden');
//         } else {
//             console.log('Hiding tem-wrp');
//             temWrpElement.style.display = 'none';
//             temWrpElement.classList.add('hidden');
//         }
//     }

//     // --- Override Shopify's Quantity Selector Component ---
//     // Wait a bit for Shopify's components to initialize
//     setTimeout(() => {
//         const quantitySelector = document.querySelector('quantity-selector-component');
        
//         if (quantitySelector && quantitySelector.increaseQuantity && quantitySelector.decreaseQuantity) {
//             console.log('Found quantity selector component, overriding methods...');
            
//             // Store original methods
//             const originalIncrease = quantitySelector.increaseQuantity.bind(quantitySelector);
//             const originalDecrease = quantitySelector.decreaseQuantity.bind(quantitySelector);
            
//             // Override increaseQuantity
//             quantitySelector.increaseQuantity = function(event) {
//                 originalIncrease(event);
//                 updateTemWrpVisibility();
//             };
            
//             // Override decreaseQuantity
//             quantitySelector.decreaseQuantity = function(event) {
//                 originalDecrease(event);
//                 updateTemWrpVisibility();
//             };
            
//             console.log('Quantity methods overridden successfully');
//         } else {
//             console.warn('Quantity selector component not found or methods not available');
//         }
//     }, 500);

//     // --- Event Listeners for Custom Field Validation ---
//     // Attach the validation function to run whenever an input changes.
//     namesTitlesInput.addEventListener('keyup', updateButtonState);
//     namesTitlesInput.addEventListener('change', updateButtonState);

//     // File input changes immediately upon selection
//     logoUploadInput.addEventListener('change', updateButtonState);

//     // Checkbox changes on click/toggle
//     sendLogoLaterCheckbox.addEventListener('change', updateButtonState);

//     // --- Fallback: Monitor quantity changes ---
//     if (quantityInput) {
//         quantityInput.addEventListener('change', updateTemWrpVisibility);
//         quantityInput.addEventListener('input', updateTemWrpVisibility);
//     }
    
//     // Run initial checks on page load to set the correct state
//     updateButtonState();
//     updateTemWrpVisibility();
    
//     // Debug logs
//     console.log('tem-wrp element found:', !!temWrpElement);
//     console.log('quantity input found:', !!quantityInput);
// }, 1000);
// });

// document.addEventListener('DOMContentLoaded', () => {

//     // --- Configuration ---
//     const ADD_TO_CART_BUTTON_ID = 'BuyButtons-ProductSubmitButton-AaVNieDRtcFQvN0pqc__add-to-cart';
    
//     const originalButtonText = 'Add to cart';
//     const disabledButtonText = 'Fill details to proceed';

//     const namesTitlesInput = document.getElementById('names-titles');
//     // const logoUploadInput = document.getElementById('artwork-upload1'); 
//     const sendLogoLaterCheckbox = document.getElementById('send-logo-later');
    
//     const addToCartButton = document.getElementById(ADD_TO_CART_BUTTON_ID);
//     const addToCartTextContent = addToCartButton ? addToCartButton.querySelector('.add-to-cart-text__content') : null;
    
//     const quantityInput = document.querySelector('input[name="quantity"]');
//     const temWrpElement = document.getElementById('tem-wrp');
    
//     setTimeout(() => {
//       const logoUploadInput = document.querySelector('.filepond--browser'); 
//       if (!namesTitlesInput || !logoUploadInput || !sendLogoLaterCheckbox || !addToCartButton || !addToCartTextContent) {
//           console.error('Custom validation script: One or more required elements were not found.');
//           return;
//       }

//       function validateCustomFields() {
//           const isNamesTitlesFilled = namesTitlesInput.value.trim().length > 0;
//           const isLogoUploaded = document.querySelector('.filepond--browser').files.length > 0;
//           const isSendingLaterChecked = sendLogoLaterCheckbox.checked;
//           const isLogoRequirementMet = isLogoUploaded || isSendingLaterChecked;

//           return isNamesTitlesFilled && isLogoRequirementMet;
//       }

//       function updateButtonState() {
//           console.log('🔄 updateButtonState called');
//           const isValid = validateCustomFields();

//           if (isValid) {
//               addToCartButton.disabled = false;
//               addToCartButton.style.opacity = '1';
//               addToCartButton.style.cursor = 'pointer'; 
              
//               if (addToCartTextContent.textContent !== originalButtonText) {
//                   addToCartTextContent.textContent = originalButtonText;
//               }
//           } else {
//               addToCartButton.disabled = true;
//               addToCartButton.style.opacity = '0.6';
//               addToCartButton.style.cursor = 'not-allowed';
              
//               if (addToCartTextContent.textContent !== disabledButtonText) {
//                   addToCartTextContent.textContent = disabledButtonText;
//               }
//           }
//       }

//       function updateTemWrpVisibility() {
//           if (!temWrpElement || !quantityInput) return;
          
//           const quantity = parseInt(quantityInput.value, 10);
//           console.log('Current quantity:', quantity);
          
//           if (quantity > 1) {
//               temWrpElement.style.display = 'block';
//               temWrpElement.classList.remove('hidden');
//           } else {
//               temWrpElement.style.display = 'none';
//               temWrpElement.classList.add('hidden');
//           }
//       }

//       // --- Override Shopify's Quantity Selector Component ---
//       setTimeout(() => {
//           const quantitySelector = document.querySelector('quantity-selector-component');
          
//           if (quantitySelector && quantitySelector.increaseQuantity && quantitySelector.decreaseQuantity) {
//               console.log('Found quantity selector component, overriding methods...');
              
//               const originalIncrease = quantitySelector.increaseQuantity.bind(quantitySelector);
//               const originalDecrease = quantitySelector.decreaseQuantity.bind(quantitySelector);
              
//               quantitySelector.increaseQuantity = function(event) {
//                   originalIncrease(event);
//                   updateTemWrpVisibility();
//               };
              
//               quantitySelector.decreaseQuantity = function(event) {
//                   originalDecrease(event);
//                   updateTemWrpVisibility();
//               };
              
//               console.log('Quantity methods overridden successfully');
//           }
//       }, 500);

//       // --- Event Listeners ---
//       namesTitlesInput.addEventListener('keyup', updateButtonState);
//       namesTitlesInput.addEventListener('change', updateButtonState);
//       logoUploadInput.addEventListener('change', updateButtonState);
//       sendLogoLaterCheckbox.addEventListener('change', updateButtonState);

//       if (quantityInput) {
//           quantityInput.addEventListener('change', updateTemWrpVisibility);
//           quantityInput.addEventListener('input', updateTemWrpVisibility);
//       }
      
//       updateButtonState();
//       updateTemWrpVisibility();

//       // --- DIAGNOSTIC: Log ALL events on document ---
//       console.log('🔍 Setting up event monitoring...');
      
//       // Capture ALL events for 10 seconds to see what fires on variant change
//       const eventsToMonitor = ['change', 'click', 'variant:change', 'variantChange', 
//                               'product:variant-change', 'variantChanged', 'update:variant'];
      
//       eventsToMonitor.forEach(eventName => {
//           document.addEventListener(eventName, (e) => {
//               console.log(`📢 Event detected: ${eventName}`, e.target);
//           }, true); // Use capture phase
//       });

//       // Monitor changes to the add-to-cart button
//       if (addToCartButton) {
//           const observer = new MutationObserver((mutations) => {
//               console.log('🔔 Button mutation detected:', mutations);
//               // Check if button was re-enabled by Shopify
//               if (!addToCartButton.disabled) {
//                   console.log('⚠️ Button was enabled, re-running validation...');
//                   updateButtonState();
//               }
//           });
          
//           observer.observe(addToCartButton, {
//               attributes: true,
//               attributeOldValue: true,
//               childList: true,
//               subtree: true
//           });
//       }

//       // Monitor the product form
//       const productForm = document.querySelector('form[action*="/cart/add"]');
//       if (productForm) {
//           console.log('✅ Product form found:', productForm);
//           productForm.addEventListener('change', (event) => {
//               console.log('📝 Form change detected on:', event.target.name, event.target);
//           }, true);
//       } else {
//           console.log('❌ Product form NOT found');
//       }

//       console.log('✅ Script initialized. Now change a variant and watch the console!');

//     }, 1000);
// });


document.addEventListener('DOMContentLoaded', () => {
    const CONFIG = {
        buttonId: 'BuyButtons-ProductSubmitButton-AaVNieDRtcFQvN0pqc__add-to-cart',
        originalText: 'Add to cart',
        disabledText: 'Fill details to proceed',
        textInputId: 'names-titles',
        checkboxId: 'send-logo-later'
    };

    setTimeout(() => {
        const namesInput = document.getElementById(CONFIG.textInputId);
        const laterCheckbox = document.getElementById(CONFIG.checkboxId);
        const cartBtn = document.getElementById(CONFIG.buttonId);
        const btnText = cartBtn ? cartBtn.querySelector('.add-to-cart-text__content') : null;

        if (!namesInput || !cartBtn) return;

        function validateForm() {
            // 1. Check Text Field
            const hasText = namesInput.value.trim().length > 0;

            // 2. Check Logo 
            // We check the FilePond status by looking for 'filepond--item' elements in the DOM
            const filePondItems = document.querySelectorAll('.filepond--item');
            const hasFile = filePondItems.length > 0;
            
            // 3. Check Checkbox
            const isSendLater = laterCheckbox ? laterCheckbox.checked : false;

            const isLogoValid = hasFile || isSendLater;
            // const isFormValid = hasText && isLogoValid;
            const isFormValid = hasText;

            console.log(`Validation: Text(${hasText}), File(${hasFile}), Later(${isSendLater})`);

            if (isFormValid) {
                cartBtn.disabled = false;
                cartBtn.style.opacity = '1';
                cartBtn.style.cursor = 'pointer';
                if (btnText) btnText.textContent = CONFIG.originalText;
            } else {
                cartBtn.disabled = true;
                cartBtn.style.opacity = '0.6';
                cartBtn.style.cursor = 'not-allowed';
                if (btnText) btnText.textContent = CONFIG.disabledText;
            }
        }

        // --- Event Listeners ---

        // Listen for typing
        namesInput.addEventListener('input', validateForm);

        // Listen for checkbox
        if (laterCheckbox) {
            laterCheckbox.addEventListener('change', validateForm);
        }

        // --- FILEPOND SPECIFIC EVENTS ---
        // FilePond fires these events on the document when a file is added or removed
        document.addEventListener('FilePond:addfile', () => {
            console.log('File added to Pond');
            setTimeout(validateForm, 100); // Small delay to let DOM update
        });

        document.addEventListener('FilePond:removefile', () => {
            console.log('File removed from Pond');
            setTimeout(validateForm, 100);
        });

        // Backup: Mutation observer focusing specifically on the list of files
        const pondRoot = document.querySelector('.filepond--root');
        if (pondRoot) {
            const observer = new MutationObserver(validateForm);
            observer.observe(pondRoot, { childList: true, subtree: true });
        }

        // Initial check
        validateForm();

    }, 1500);
});